import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@neo/primitives/sheet'
import { Typography } from '@neo/primitives/typography'
import { pluralize } from '@shared/utils'
import { Search, X } from 'lucide-react'
import { useState } from 'react'
import { usePermissions } from 'v2/shared/hooks/use-permissions'
import type { RUserSession } from 'v2/types/api/auth'
import { RbacModules, RbacPermissionTypes } from 'v2/types/rbac'
import { isAciExecutionTarget } from '../utils/integration-availability'
import type { AccountMenuAction } from './account-actions-menu'
import { AccountsList, SectionTitle } from './accounts-table'
import { AppLogo } from './catalog-row'
import type { ExternalAppRow } from './external-app-actions'
import { connections } from './external-apps-rollup'
import { useReconnectApp } from './hooks/use-reconnect-app'
import { openConnectPage } from './integration-side-sheet'

interface IntegrationDetailSheetProps {
  app: ExternalAppRow
  currentUser?: RUserSession
  aciEnabled: boolean
  onClose: () => void
  /** The page owns the confirm dialogs and mutations. */
  onAccountAction: (
    action: 'enable' | 'disable' | 'delete' | 'revoke',
    connection: ExternalAppRow,
  ) => void
  onShare: (connection: ExternalAppRow) => void
  onCustomFields: (connection: ExternalAppRow) => void
}

// Custom MCP servers are registered, not picked from the catalog, so the connect page has none.
const isCustomMcpTool = (app: ExternalAppRow) => String(app.app_category) === 'Custom MCP'

// Its own `<Sheet>`, not `SheetProvider`: the provider holds one `sheetData` and would fight the
// catalog sheet for it.
export const IntegrationDetailSheet = ({
  app,
  currentUser,
  aciEnabled,
  onClose,
  onAccountAction,
  onShare,
  onCustomFields,
}: IntegrationDetailSheetProps) => {
  const [actionSearch, setActionSearch] = useState('')
  const { reconnectApp } = useReconnectApp()
  const { hasPermission } = usePermissions()
  const canConnect = hasPermission(RbacModules.integration, RbacPermissionTypes.create)
  const accounts = connections(app)
  // Sibling accounts of one provider expose the same actions, so the first live one is enough.
  const live = accounts.find((connection) => isAciExecutionTarget(connection, aciEnabled))
  // A lapsed token does not change what the tool can do, so fall back to any stored list.
  const actions =
    live?.actions ?? accounts.find((account) => account.actions?.length)?.actions ?? []
  // Present, but nothing can run them until the connection is restored.
  const needsReconnect = accounts.length > 0 && !live
  const actionsDisabled = needsReconnect && actions.length > 0

  const handleAccountAction = (action: AccountMenuAction, connection: ExternalAppRow) => {
    if (action === 'share') return onShare(connection)
    if (action === 'reconnect') return reconnectApp(connection)
    if (action === 'custom-fields') return onCustomFields(connection)
    onAccountAction(action, connection)
  }
  const query = actionSearch.trim().toLowerCase()
  const matchingActions = query
    ? actions.filter(
        (action) =>
          action.action_name.toLowerCase().includes(query) ||
          action.description?.toLowerCase().includes(query),
      )
    : actions

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent
        detached
        // The primitive's absolutely-positioned X would overlap the header's own controls.
        showClose={false}
        className="flex w-[65%] flex-col sm:w-[65%] md:w-[65%] lg:max-w-[550px]"
        // Nothing here wants a focused field on open.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <SheetHeader className="flex-row items-start gap-3 space-y-0">
          <AppLogo app={app} />
          <div className="min-w-0 flex-1">
            <SheetTitle className="truncate text-h3-600">{app.name}</SheetTitle>
            <Typography variant="XS/400" className="mt-0.5 text-tertiary">
              {pluralize(accounts.length, 'account')}
            </Typography>
          </div>
          <div className="flex flex-none items-center gap-1 pt-0.5">
            <Button
              type="button"
              variant="ghost"
              className="size-8 shrink-0 p-0 hover:bg-hv1"
              onClick={onClose}
              data-testid="button-close-detail-sheet"
              aria-label="Close"
            >
              <X className="size-4 text-secondary" />
            </Button>
          </div>
        </SheetHeader>

        <ScrollArea hideScrollbar className="-mx-1 flex-1 px-1">
          <div className="pt-4 pb-4">
            {accounts.length === 0 ? (
              <Typography variant="S/400" className="py-3 text-tertiary">
                No accounts are connected to this tool yet.
              </Typography>
            ) : (
              <AccountsList
                accounts={accounts}
                currentUser={currentUser}
                aciEnabled={aciEnabled}
                onAction={handleAccountAction}
                onAddAccount={
                  canConnect && !isCustomMcpTool(app) ? () => openConnectPage(app) : undefined
                }
              />
            )}

            <div className="mt-6">
              {/* `bg-background` is load-bearing: without it the list scrolls through the
                  sticky row. */}
              <div className="sticky top-0 z-10 flex flex-col gap-3 bg-background py-2">
                <div className="flex items-baseline gap-2">
                  <SectionTitle>Actions</SectionTitle>
                  <Typography variant="S/400" className="text-tertiary">
                    {actions.length}
                  </Typography>
                </div>
                {/* The search only earns its space once the list is long enough to scan. */}
                {actions.length > 6 && (
                  <Input
                    placeholder="Search actions"
                    startIcon={Search}
                    value={actionSearch}
                    onChange={(event) => setActionSearch(event.target.value)}
                    data-testid="input-search-actions"
                  />
                )}
              </div>
              {actions.length === 0 ? (
                <Typography variant="S/400" className="py-3 text-tertiary">
                  {accounts.length === 0
                    ? 'Connect an account to see the actions it can perform.'
                    : needsReconnect
                      ? 'No actions are available. Reconnect this tool to load them.'
                      : 'This tool exposes no actions yet.'}
                </Typography>
              ) : (
                <div className="flex flex-col gap-2" data-testid="list-actions">
                  {matchingActions.map((action) => (
                    <div
                      key={action.action_name}
                      // Dimmed, not hidden: the reader judges a reconnect against this list.
                      className={cn('rounded-md bg-bg2 px-3 py-2', actionsDisabled && 'opacity-80')}
                    >
                      <Typography
                        variant="S/600"
                        className={cn(
                          'font-mono',
                          actionsDisabled ? 'text-secondary' : 'text-foreground',
                        )}
                      >
                        {action.action_name}
                      </Typography>
                      {action.description && (
                        <Typography variant="XS/400" className="mt-0.5 text-tertiary">
                          {action.description}
                        </Typography>
                      )}
                    </div>
                  ))}
                  {matchingActions.length === 0 && (
                    <Typography variant="S/400" className="py-2 text-tertiary">
                      No actions match &ldquo;{actionSearch}&rdquo;.
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
